import {UserAvatarImage} from '#/components/user/user-avatar-image';
import {useIsUserAccount} from '#/hooks/use-is-user-account';
import {useUserAvatar} from '#/hooks/use-user-avatar';
import {useUserDisplayName} from '#/hooks/use-user-display-name';
import {PortfolioTab} from '#/navigation/tabs';
import {getExchangeRouteWithQueryParams} from '#/routes/exchange/get-exchange-route-with-query-params';
import {ComponentProps, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {UserProfile} from 'shareland-common';
import {Hex} from 'viem';

type UserAvatarNameProps = {
  account: Hex;
  userProfile?: UserProfile;
};

export const UserAvatarName: FunctionComponent<UserAvatarNameProps> = ({account, userProfile}) => {
  const {t} = useTranslation('translation');

  const {data: userAvatar} = useUserAvatar({account, userProfile});
  const {data: userDisplayName} = useUserDisplayName({account, userProfile});
  const isUserAccount = useIsUserAccount(account);

  return (
    <div className='grow flex space-x-3 text-sm hover:opacity-70'>
      <div className='grow-0 shrink-0 w-10 h-10 relative'>
        <UserAvatarImage
          avatarUrl={userAvatar}
          displayName={userDisplayName}
          widthClassName='w-full h-full'
          whiteBorder={false}
        />
        {isUserAccount && (
          <span className='absolute -bottom-2 left-0 w-full'>
            <div className='w-fit mx-auto bg-upgreen font-semibold text-white text-xs p-0.5 rounded'>
              {t('components.userProfile.signedInUserIndicator')}
            </div>
          </span>
        )}
      </div>

      <span className='inline-flex align-middle font-bold text-white text-sm items-center gap-2 group-hover:underline transition-all duration-100'>
        <span className='break-words overflow-hidden'>{userDisplayName}</span>
      </span>
    </div>
  );
};

type LinkToUserPortfolioProps = {
  account: string;
} & Omit<ComponentProps<typeof Link>, 'to'>;

export const LinkToUserPortfolio: FunctionComponent<LinkToUserPortfolioProps> = ({
  account,
  children,
  className,
  ...rest
}) => {
  const profileLinkTo = getExchangeRouteWithQueryParams(`/portfolio/${account}`, {
    portfolioTabId: PortfolioTab.ASSETS,
  });

  return (
    <Link to={profileLinkTo} className={`hover:opacity-70 ${className}`} {...rest}>
      {children}
    </Link>
  );
};
